<template>
    <div class="hotlist-container">
      <div class="platform-nav">
        <div class="nav-scroll">
          <button
            v-for="platform in platformList"
            :key="platform.name"
            @click="selectPlatform(platform)"
            :class="{ active: selectedPlatform?.name === platform.name }"
            class="platform-tab"
          >
            <span class="name">{{ platform.name }}</span>
            <span class="subtitle">{{ platform.subtitle }}</span>
          </button>
        </div>
      </div>
  
      <div class="content-box">
        <transition name="fade-slide" mode="out-in">
          <div class="dynamic-container" :key="currentViewKey">
            <div v-if="loading" class="loading-state">
              <div class="pulse-loader"></div>
              <p>加载中...</p>
            </div>
  
            <div v-else-if="error" class="error-state">
              <div class="error-icon">⚠️</div>
              <p>{{ error }}</p>
              <button @click="fetchHotList">重试</button>
            </div>
  
            <template v-else>
              <div class="platform-header">
                <span class="update-time">
                  {{ formatTime(selectedPlatform?.update_time) }}
                </span>
              </div>
              
              <div class="hot-items">
                <a
                  v-for="(item, index) in selectedPlatform?.data"
                  :key="index"
                  :href="item.url || item.mobil_url"
                  target="_blank"
                  rel="noopener"
                  class="hot-item"
                >
                  <div class="item-rank">{{ index + 1 }}</div>
                  <div class="item-main">
                    <div class="title-row">
                      <span class="title">{{ item.title }}</span>
                      <span class="heat">{{ formatHeat(item.heat) }}</span>
                    </div>
                  </div>
                </a>
              </div>
            </template>
          </div>
        </transition>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed } from 'vue'
  import axios from 'axios'
  
  const API_URL = 'https://api.vvhan.com/api/hotlist/all'
  
  const platformList = ref([])
  const selectedPlatform = ref(null)
  const loading = ref(false)
  const error = ref(null)
  
  const formatHeat = (value) => {
    try {
      if (typeof value !== 'string') value = String(value || '0')
      if (value.includes('万')) {
        const numValue = parseFloat(value.replace('万', '')) * 10000
        return numValue >= 1000000 ? `${(numValue/1000000).toFixed(1)}m` : `${(numValue/1000).toFixed(1)}k`
      }
      const num = parseFloat(value.replace(/[^0-9.]/g, '')) || 0
      if (num >= 1000000) return `${(num/1000000).toFixed(1)}m`
      if (num >= 1000) return `${(num/1000).toFixed(1)}k`
      return num || '0'
    } catch {
      return ''
    }
  }
  
  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    const date = new Date(timeStr)
    return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')} 更新`
  }
  
  const currentViewKey = computed(() => loading.value ? 'loading' : error.value ? 'error' : 'content')
  
  const selectPlatform = (platform) => {
    selectedPlatform.value = platform
  }
  
  const fetchHotList = async () => {
    try {
      loading.value = true
      error.value = null
      const { data } = await axios.get(API_URL, { timeout: 10000 })
      if (data?.success && data.data?.length) {
        platformList.value = data.data
        selectedPlatform.value = data.data[0]
      } else throw new Error('数据获取失败')
    } catch (err) {
      error.value = err.response?.data?.message || '网络异常'
    } finally {
      loading.value = false
    }
  }
  
  onMounted(fetchHotList)
  </script>
  
  <style scoped>
  .hotlist-container {
    width: 95vw;
    height: 100vh;
    margin: 0 auto;
    padding: 0 10px;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-height: 80vh;
  }
  
  .platform-nav {
    border-bottom: 1px solid rgba(255,107,107,0.1);
    margin-bottom: 12px;
    overflow-x: auto;
    flex-shrink: 0;
  }
  
  .nav-scroll {
    display: flex;
    gap: 9px;
    padding-bottom: 2px;
  }
  
  .platform-tab {
    min-width: 88px;
    padding: 8px 12px;
    border: none;
    border-radius: 6px 6px 0 0;
    background: mistyrose;
    color: #666;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: background 0.2s;
  }
  
  .platform-tab.active {
    background: #ff6b6b;
    color: white;
  }
  
  .name {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }
  
  .subtitle {
    font-size: 12px;
    opacity: 0.7;
  }
  
  .content-box {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .dynamic-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .platform-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 0 12px;
    flex-shrink: 0;
  }
  
  .platform-header h2 {
    font-size: 18px;
    margin: 0;
    color: #333;
  }
  
  .update-time {
    color: #999;
    font-size: 13px;
  }
  
  .hot-items {
    flex: 1;
    display: grid;
    gap: 8px;
    padding: 0 12px 12px;
    overflow-y: auto;
  }
  
  .hot-item {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    text-decoration: none;
    color: #333;
    transition: transform 0.2s;
    flex-shrink: 0;
  }
  
  .hot-item:hover {
    transform: translateY(-1px);
  }
  
  .item-rank {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #ff6b6b;
    color: white;
    font-weight: bold;
    font-size: 14px;
    margin-right: 12px;
  }
  
  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  
  .title {
    flex: 1;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 8px;
  }
  
  .heat {
    color: #ff6b6b;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  .meta-row {
    margin-top: 4px;
  }
  
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    flex: 1;
  }
  
  .pulse-loader {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ff6b6b;
    animation: pulse 1.5s infinite;
    margin-bottom: 12px;
  }
  
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    flex: 1;
  }
  
  .error-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  .error-state button {
    background: #ff6b6b;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 20px;
    cursor: pointer;
  }
  
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.3s;
  }
  
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(10px);
  }
  
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
  
  @media (max-width: 768px) {
    .hotlist-container {
      width: 100%;
      padding: 0 8px;
    }
  
    .platform-tab {
      min-width: 72px;
      padding: 6px 10px;
    }
  
    .title {
      font-size: 13px;
    }
  
    .heat {
      display: none;
    }
  
    .hot-items {
      padding: 0 8px 8px;
    }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(0.9); opacity: 0.6; }
    50% { transform: scale(1); opacity: 1; }
  }
  </style>
  